import { buildSaleReference, normalizeArabic, normalizeDigits, type SaleInput } from '@najah/core'
import { api } from '../api'
import { getDb, enqueue, saveLocalSale, nextSaleCounter } from './localDb'
import { getCachedDeviceInfo, pushOutbox, registerDevice, type DeviceInfo } from './syncEngine'
import { isTauri } from './platform'

export interface PosProduct {
  id: string
  sku: string
  barcode: string | null
  nameAr: string
  unit: string
  retailPrice: number
  costPrice: number
  taxRate: number
  quantity: number
  trackStock: boolean
}

export interface SubmitResult {
  /** الفاتورة حُفظت محلياً وتنتظر المزامنة (وضع دون اتصال) */
  queued: boolean
  reference: string
}

export interface PosDataSource {
  readonly offlineCapable: boolean
  search(term: string): Promise<PosProduct[]>
  findByBarcode(code: string): Promise<PosProduct | null>
  nextReference(): Promise<string>
  submitSale(sale: SaleInput): Promise<SubmitResult>
}

/* ══════════ متصفّح: الخادم مباشرةً ══════════ */
class RemoteDataSource implements PosDataSource {
  readonly offlineCapable = false
  private device: DeviceInfo | null = null

  private async deviceInfo(): Promise<DeviceInfo> {
    this.device ??= await registerDevice()
    return this.device
  }

  async search(term: string): Promise<PosProduct[]> {
    const res = await api<{ items: PosProduct[] }>('/products', {
      query: { search: term, pageSize: 20, isActive: 'yes' },
    })
    return res.items
  }

  async findByBarcode(code: string): Promise<PosProduct | null> {
    try {
      return await api<PosProduct>(`/products/barcode/${encodeURIComponent(code)}`)
    } catch {
      return null
    }
  }

  async nextReference(): Promise<string> {
    const info = await this.deviceInfo()
    const today = new Date().toISOString().slice(0, 10)
    const storageKey = `najah.saleCounter.${today}`
    let counter = 1
    try {
      counter = Number(localStorage.getItem(storageKey) ?? '0') + 1
      localStorage.setItem(storageKey, String(counter))
    } catch {
      // تخزين معطّل: نعتمد على وقت اليوم لتفادي التكرار داخل نفس الجلسة
      counter = Math.floor((Date.now() % 86_400_000) / 1000)
    }
    return buildSaleReference(info.storeCode, info.deviceCode, new Date(), counter)
  }

  async submitSale(sale: SaleInput): Promise<SubmitResult> {
    await api('/sales', { method: 'POST', body: sale })
    return { queued: false, reference: sale.reference }
  }
}

/* ══════════ سطح المكتب: SQLite محلي أولاً ══════════ */
class LocalDataSource implements PosDataSource {
  readonly offlineCapable = true

  private async deviceInfo(): Promise<DeviceInfo> {
    const cached = await getCachedDeviceInfo()
    if (cached) return cached
    // أول تشغيل يحتاج اتصالاً مرة واحدة لتسجيل الجهاز والحصول على رمزه
    return registerDevice()
  }

  private async query(where: string, params: unknown[]): Promise<PosProduct[]> {
    const db = await getDb()
    const rows = await db.select<Record<string, unknown>[]>(
      `SELECT p.id, p.sku, p.barcode, p.nameAr, p.unit, p.retailPrice, p.costPrice, p.taxRate,
              p.trackStock, COALESCE(l.quantity, 0) AS quantity
         FROM products p
         LEFT JOIN inventory_levels l ON l.productId = p.id
        WHERE p.deletedAt IS NULL AND p.isActive = 1 AND ${where}
        LIMIT 20`,
      params,
    )
    return rows.map((r) => ({
      id: r.id as string,
      sku: r.sku as string,
      barcode: (r.barcode as string) ?? null,
      nameAr: r.nameAr as string,
      unit: r.unit as string,
      retailPrice: Number(r.retailPrice),
      costPrice: Number(r.costPrice),
      taxRate: Number(r.taxRate),
      quantity: Number(r.quantity),
      trackStock: Number(r.trackStock) === 1,
    }))
  }

  search(term: string): Promise<PosProduct[]> {
    // نبحث بالمفتاح المطبَّع نفسه الذي يبنيه الخادم، فتتطابق النتائج في الوضعين
    const normalized = `%${normalizeArabic(term)}%`
    const digits = `%${normalizeDigits(term)}%`
    return this.query('(p.searchKey LIKE $1 OR p.sku LIKE $2 OR p.barcode LIKE $2)', [normalized, digits])
  }

  async findByBarcode(code: string): Promise<PosProduct | null> {
    const normalized = normalizeDigits(code.trim())
    const rows = await this.query('(p.barcode = $1 OR p.sku = $1)', [normalized])
    return rows[0] ?? null
  }

  async nextReference(): Promise<string> {
    const info = await this.deviceInfo()
    const counter = await nextSaleCounter()
    return buildSaleReference(info.storeCode, info.deviceCode, new Date(), counter)
  }

  /**
   * محلي أولاً: تُكتب الفاتورة في SQLite وتدخل الطابور دائماً،
   * ثم نحاول الدفع. فشل الشبكة لا يُفشل البيع — البضاعة خرجت والزبون دفع.
   */
  async submitSale(sale: SaleInput): Promise<SubmitResult> {
    const db = await getDb()

    await saveLocalSale(
      { id: sale.id, reference: sale.reference, total: sale.total, occurredAt: sale.occurredAt },
      sale,
    )
    await enqueue(sale.id, 'sale', sale)

    // خصم الرصيد محلياً فوراً حتى تعكس الشاشة الواقع قبل المزامنة التالية
    for (const item of sale.items) {
      await db.execute(
        `INSERT INTO inventory_levels (productId, quantity) VALUES ($1, $2)
         ON CONFLICT(productId) DO UPDATE SET quantity = quantity - $3`,
        [item.productId, -item.quantity, item.quantity],
      )
    }

    try {
      await pushOutbox()
      return { queued: false, reference: sale.reference }
    } catch {
      return { queued: true, reference: sale.reference }
    }
  }
}

let cached: PosDataSource | null = null

export function getPosDataSource(): PosDataSource {
  cached ??= isTauri() ? new LocalDataSource() : new RemoteDataSource()
  return cached
}
